import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import Analytics from '../../../analytics/Analytics';
import Ad from '../../../models/banner/Ad';
import { modelOf } from '../../../prop-types';
import ConfigStore from '../../../store/ConfigStore';
import UIStore from '../../../store/UIStore';
import AdZones from '../../../types/AdZones';
import LinkType from '../../../types/LinkType';
import BannerType from '../../../types/banner/BannerType';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import ProductCode from '../../product/ProductCode';
import ProductPrice from '../../product/ProductPrice';
import ProductImageSign from '../../product/ProductSign';
import ProductSignDiscount from '../../product/ProductSignDiscount';
import ProductSigns from '../../product/ProductSigns';
import BannerMediaImage from './components/BannerMediaImage';
import BannerMediaVideo from './components/BannerMediaVideo';
import useBannerInteractions from './hook';

const Banner = ({
  configStore,
  uiStore,
  analytics,
  banner,
  bannerZone,
  onVideoPlaybackChange,
  className,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [handleInteraction, handleInteractionDown] = useBannerInteractions();

  const sendView = () => {
    analytics.promoView([
      {
        bannerZone,
        banner,
      },
    ]);
  };

  if (!isMounted) {
    !configStore.analytics.ga4.enabled && banner && sendView();
  }

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const sendPromotionClick = () => {
    analytics.promotionClick([
      {
        bannerZone,
        banner,
      },
    ]);
  };

  const renderBannerWrapper = () => {
    const bannerWrapper = uiStore.isSmallest
      ? 'Banner__wrapper-mobile'
      : 'Banner__wrapper';

    const type = banner.is_external
      ? LinkType.EXTERNAL_LINK
      : LinkType.INTERNAL_LINK;

    const Wrapper = ({ children }) => (
      <div className={bannerWrapper}>
        {banner.link_url ? (
          <MaybeExternalLink
            accessible={false}
            to={banner.link_url}
            type={type}
            onMouseDown={handleInteractionDown}
            onTouchStart={handleInteractionDown}
            onClick={handleInteraction}
          >
            {children}
          </MaybeExternalLink>
        ) : (
          children
        )}
      </div>
    );

    return (
      <Wrapper>
        {renderContent()}
        {renderBannerOverlay()}
      </Wrapper>
    );
  };

  const renderContent = () => {
    if (banner.type === BannerType.VIDEO) {
      return (
        <BannerMediaVideo
          banner={banner}
          onVideoPlaybackChange={onVideoPlaybackChange}
        />
      );
    }

    return <BannerMediaImage banner={banner} isSmallest={uiStore.isSmallest} />;
  };

  const renderBannerOverlay = () => {
    const title = getBannerTitle(banner);
    const content = getBannerContent(banner);
    const wrapperAlign = banner.stylesAlign;
    const priceInformation = getBannerPriceInformation(banner);

    return (
      (title || content) && (
        <div
          className="Banner__overlay"
          style={{
            background: banner.overlay_background_color,
            color: banner.text_color || undefined,
            pointerEvents:
              banner.type === BannerType.VIDEO ? 'none' : undefined,
            textAlign: wrapperAlign.textAlign || undefined,
          }}
        >
          {banner.overlay_image && (
            <img src={banner.overlay_image} alt={banner.seo_description} />
          )}
          {getBannerContentWrapper(
            title,
            banner,
            priceInformation,
            content,
            wrapperAlign
          )}
        </div>
      )
    );
  };

  const getBannerTitle = (localBanner) => {
    if (!localBanner.title) return;

    return (
      <div className="Banner__title">
        {localBanner.manufacturer && (
          <div className="Banner__manufacturer">{localBanner.manufacturer}</div>
        )}
        <h3 style={localBanner.stylesTitle(uiStore.isSmallest)}>
          {localBanner.title}
        </h3>
      </div>
    );
  };

  const getBannerContent = (localBanner) => {
    if (!localBanner.content) return;

    return (
      <div
        className="Banner__content"
        style={localBanner.stylesContent(uiStore.isSmallest)}
      >
        {localBanner.content}
      </div>
    );
  };

  const getBannerDiscountText = (localBanner) =>
    localBanner.discount_text ? (
      <div className="Banner__discount-text">
        <ProductImageSign>
          <ProductSignDiscount discountText={localBanner.discount_text} />
        </ProductImageSign>
      </div>
    ) : null;

  const getBannerPriceInformation = (localBanner) =>
    localBanner.price_info ? (
      <div className="Banner__price-info">
        <ProductPrice priceInfo={localBanner.price_info} />
        <ProductSigns
          hasDiscount={localBanner.price_info.is_discount}
          dateAdded={localBanner.date_added}
          discountProps={{
            percent: localBanner.price_info.discount_percentage,
            withUpTo: false,
            singlePriceLabel: false,
          }}
        />
      </div>
    ) : (
      getBannerDiscountText(localBanner)
    );

  const getBannerContentWrapper = (
    title,
    localBanner,
    priceInformation,
    content,
    style
  ) => (
    <div
      className="Banner__content_wrapper"
      style={{
        justifyContent: style?.justifyContent,
      }}
    >
      <div className="Banner__texts">
        {title}
        {localBanner.product_id && (
          <ProductCode code={localBanner.productCode} />
        )}
        {priceInformation}
        {content}
      </div>
    </div>
  );

  const shouldShowScent = () => {
    return (
      (bannerZone === AdZones.MAIN_BANNER ||
        bannerZone === AdZones.FOOTER_BANNER) &&
      !!configStore.banner.scent.pattern &&
      (!hasVideo() || (hasVideo() && !configStore.banner.scent.onTop)) &&
      !(uiStore.isMobile && !configStore.banner.scent.shouldShowMobile)
    );
  };

  const getBannerScent = () => {
    let onTop = {};

    if (!shouldShowScent()) {
      return null;
    }

    if (configStore.banner.scent.onTop) {
      onTop.position = 'relative';
      onTop.top = '-50px';
    }
    onTop.backgroundPositionX = configStore.banner.scent.positionX;
    onTop.backgroundRepeat = configStore.banner.scent.repeat;

    return (
      <div
        className="Banner__scent"
        style={{
          backgroundImage: `url(${configStore.banner.scent.pattern})`,
          ...onTop,
        }}
      />
    );
  };

  const hasVideo = () => {
    return (
      banner.type === BannerType.VIDEO &&
      (!!banner.video_file || !!banner.video_link)
    );
  };

  const hasMedia = () => {
    const hasImage = !!banner.image;
    return hasImage || hasVideo();
  };

  return (
    <div
      className={classNames(
        'Banner',
        {
          'Banner--default-width': !hasMedia(),
          'Banner--text-color-override': !!banner.text_color,
        },
        className
      )}
      onClick={sendPromotionClick}
    >
      {renderBannerWrapper()}
      {getBannerScent()}
    </div>
  );
};

Banner.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  banner: modelOf(Ad).isRequired,
  bannerZone: PropTypes.string.isRequired,
  onVideoPlaybackChange: PropTypes.func,
  className: PropTypes.string,
};

export default inject('configStore', 'uiStore', 'analytics')(observer(Banner));
